import React, { useCallback, useEffect, useMemo, useState } from "react";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import StarIcon from "@mui/icons-material/Star";
import AppsIcon from "@mui/icons-material/Apps";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Skeleton,
    Typography,
} from "@mui/material";
import slidingPuzzleQuestIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import {
    SHOW_SLIDING_APP_STORE_BADGE,
    SHOW_SLIDING_GOOGLE_PLAY_BADGE,
} from "../../config/nativeAppsVisibility";
import AppStoreBadgeLink from "../components/AppStoreBadgeLink";
import AppStorePromoPill from "../components/AppStorePromoPill";
import {
    browsePagerIconSx,
    browseTagChipSx,
    browseTextSx,
} from "../../dark-theme/surfaces";
import GooglePlayBadgeLink from "../components/GooglePlayBadgeLink";
import StoryReferenceWithGrid from "../../admin/story/StoryReferenceWithGrid";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import UtilService from "../../services/UtilService";
import UserService from "../../services/UserService";
import { trackEvent } from "../../services/GoogleAnalytics";
import { usePagesTranslation } from "../locales";
import PuzzleListingAbout from "../components/PuzzleListingAbout";
import BrowseTagScrollRow from "../components/BrowseTagScrollRow";
import BrowseAccessFilter, {
    accessFilterToFreeParam,
} from "../components/BrowseAccessFilter";
import { TagChipAvatar } from "../puzzles/common/components/TagChipAvatar";
import SlidingService from "../puzzles/sliding/SlidingService";
import JigsawService from "../puzzles/jigsaw/JigsawService";
import { browseViewButtonSx } from "../browse-jigsaw/JigsawBrowseStoryCard";
import SlidingCard from "./SlidingCard";
import {
    getSlidingGroupStoryId,
    getSlidingStoryTitle,
    homeSlidingGroupToCardPuzzle,
    pickSlidingBrowseBoardPreview,
    pickSlidingCardImageUrl,
    pickSlidingStoryIcon200Url,
} from "./slidingBrowseUtils";

const PAGE_SIZE = 12;
/** Sentinel for the unfiltered chip; API gets no `tags` param. */
const TAG_ALL = "all";
/** Browse pages: English puzzles only (matches crossword browse). */
const BROWSE_CONTENT_LANGUAGE = "english";

const FEED_BROWSE = "browse";
const FEED_NEW5 = "new5";
const FEED_TOP5 = "top5";
const FEED_EDITORS = "editors";
const FEATURED_LIMIT = 5;

/** Same hero layout as word-search browse; warm orange (not crossword/grid purple). */
const SLIDING_HERO_GRADIENT =
    "linear-gradient(125deg, #bf360c 0%, #e65100 28%, #f57c00 58%, #fb8c00 82%, #ffa726 100%)";

/** Filter chips on the page, same treatment as jigsaw browse. */
const heroFeaturedChipSx = (selected, tone) => {
    const tones = {
        new5: { accent: "#7dd3fc", selectedBg: "#0284c7" },
        top5: { accent: "#fbbf24", selectedBg: "#f59e0b" },
        editors: { accent: "#5eead4", selectedBg: "#0d9488" },
        all: { accent: "#fff", selectedBg: "rgba(255,255,255,0.28)" },
    };
    const c = tones[tone] || tones.top5;
    return {
        flexShrink: 0,
        fontWeight: selected ? 700 : 600,
        ...(selected
            ? {
                  bgcolor: c.selectedBg,
                  color: "#fff",
                  borderColor: tone === "all" ? "rgba(255,255,255,0.7)" : c.selectedBg,
                  "& .MuiChip-icon": { color: "#fff" },
                  "&:hover": { bgcolor: c.selectedBg, color: "#fff" },
              }
            : {
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.45)",
                  "& .MuiChip-icon": { color: c.accent },
                  "&:hover": {
                      bgcolor: "rgba(255,255,255,0.2)",
                      borderColor: c.accent,
                  },
              }),
    };
};

function tagLabelFromRow(row) {
    return typeof row?.tag === "string" ? row.tag : String(row?.tag ?? row?.name ?? "");
}

/** Chip / heading label — e.g. `animal` → `Animal`. Filter still uses the raw tag. */
function formatBrowseTagDisplayLabel(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    const lower = s.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export default function HomeSlidingBrowsePage() {
    const { t, lang } = usePagesTranslation();

    const [tags, setTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [selectedTag, setSelectedTag] = useState(TAG_ALL);
    const [feed, setFeed] = useState(FEED_NEW5);
    const [accessFilter, setAccessFilter] = useState("all");

    const [rows, setRows] = useState([]);
    /** 0-based index for `GET /home/sliding?page=` */
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [listLoading, setListLoading] = useState(true);

    const [userPayPlan, setUserPayPlan] = useState(null);
    const [userPlanResolved, setUserPlanResolved] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    useEffect(() => {
        UserService.getUserInfo()
            .then((data) => {
                if (data?.cloudUser) {
                    setUserPayPlan(UtilService.resolveWebPayPlan(data.cloudUser));
                    setSubscriptionStatus(data.cloudUser.currSub?.status || null);
                }
            })
            .catch(() => {})
            .finally(() => setUserPlanResolved(true));
    }, []);

    useEffect(() => {
        trackEvent("entered", { name: "HomeSlidingBrowsePage", puzzle_type: "SLIDING" });
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [page, selectedTag, feed]);

    useEffect(() => {
        if (!userPlanResolved) {
            setTagsLoading(true);
            return undefined;
        }
        let cancel = false;
        setTagsLoading(true);
        HomeService.getSlidingDistinctTags(BROWSE_CONTENT_LANGUAGE)
            .then((list) => {
                if (!cancel) setTags(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (!cancel) setTags([]);
            })
            .finally(() => {
                if (!cancel) setTagsLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [userPlanResolved]);

    useEffect(() => {
        if (!userPlanResolved) return undefined;
        let cancel = false;
        setListLoading(true);

        const load = async () => {
            try {
                if (feed === FEED_NEW5) {
                    const [slidingResult, jigsawResult] = await Promise.all([
                        SlidingService.listBrowse({
                            page: 0,
                            pageSize: FEATURED_LIMIT * 3,
                            language: BROWSE_CONTENT_LANGUAGE,
                            sort: "publishedAt:desc",
                            excludeCompleted: false,
                            excludePlayed: false,
                        }),
                        JigsawService.listBrowse({
                            page: 0,
                            pageSize: FEATURED_LIMIT,
                            language: BROWSE_CONTENT_LANGUAGE,
                            sort: "publishedAt:desc",
                            excludeCompleted: false,
                            excludePlayed: false,
                        }),
                    ]);
                    if (cancel) return;
                    const jigsawNewIds = new Set(
                        (Array.isArray(jigsawResult.content) ? jigsawResult.content : [])
                            .slice(0, FEATURED_LIMIT)
                            .map((group) => {
                                const sid = group?.story?.id ?? group?.storyId;
                                return typeof sid === "string" ? sid.trim() : "";
                            })
                            .filter(Boolean),
                    );
                    const slidingRows = Array.isArray(slidingResult.content) ? slidingResult.content : [];
                    const withoutJigsawNew = slidingRows.filter((group) => {
                        const sid = getSlidingGroupStoryId(group);
                        return !sid || !jigsawNewIds.has(sid);
                    });
                    setRows(withoutJigsawNew.slice(0, FEATURED_LIMIT));
                    setTotalPages(0);
                    return;
                }
                if (feed === FEED_TOP5) {
                    const result = await SlidingService.listPopular({
                        language: BROWSE_CONTENT_LANGUAGE,
                        limit: FEATURED_LIMIT,
                    });
                    if (cancel) return;
                    setRows(Array.isArray(result.content) ? result.content : []);
                    setTotalPages(0);
                    return;
                }
                if (feed === FEED_EDITORS) {
                    const result = await SlidingService.listEditorsPicks({
                        language: BROWSE_CONTENT_LANGUAGE,
                        limit: FEATURED_LIMIT,
                    });
                    if (cancel) return;
                    setRows(Array.isArray(result.content) ? result.content : []);
                    setTotalPages(0);
                    return;
                }

                const tagFilter = selectedTag === TAG_ALL ? [] : [selectedTag];
                const result = await HomeService.getSlidingPage({
                    page,
                    pageSize: PAGE_SIZE,
                    excludePlayed: true,
                    excludeCompleted: true,
                    sort: "publishedAt:desc",
                    language: BROWSE_CONTENT_LANGUAGE,
                    tags: tagFilter,
                    free: accessFilterToFreeParam(accessFilter),
                });
                if (cancel) return;
                const content = Array.isArray(result.content) ? result.content : [];
                const rawTp = result.totalPages;
                const tp =
                    typeof rawTp === "number" && Number.isFinite(rawTp)
                        ? rawTp
                        : typeof rawTp === "string" && rawTp.trim() !== ""
                          ? Number(rawTp)
                          : 0;
                setTotalPages(Number.isFinite(tp) && tp >= 0 ? tp : 0);
                setRows(content);
            } catch (err) {
                Logger.error("HomeSlidingBrowsePage:", err);
                if (cancel) return;
                setRows([]);
                setTotalPages(0);
            } finally {
                if (!cancel) setListLoading(false);
            }
        };

        load();
        return () => {
            cancel = true;
        };
    }, [userPlanResolved, page, selectedTag, feed, accessFilter]);

    const handleSelectTag = useCallback((tag) => {
        setFeed(FEED_BROWSE);
        setSelectedTag(tag);
        setPage(0);
    }, []);

    const handleSelectFeed = useCallback((nextFeed) => {
        setFeed((current) => (current === nextFeed ? FEED_BROWSE : nextFeed));
        setSelectedTag(TAG_ALL);
        setPage(0);
    }, []);

    const handleAccessFilter = useCallback((next) => {
        setAccessFilter(next);
        setPage(0);
    }, []);

    const isFeaturedFeed = feed === FEED_NEW5 || feed === FEED_TOP5 || feed === FEED_EDITORS;
    const canGoPrev = page > 0 && !listLoading && !isFeaturedFeed;
    const canGoNext = totalPages > 0 && page < totalPages - 1 && !listLoading && !isFeaturedFeed;
    const showPager = !isFeaturedFeed && totalPages > 1;

    const emptyMessage = (() => {
        if (feed === FEED_NEW5) {
            return t("puzzles-browse.empty-sliding-new5", "No new puzzles yet.");
        }
        if (feed === FEED_TOP5) {
            return t("puzzles-browse.empty-sliding-top5", "No top puzzles yet.");
        }
        if (feed === FEED_EDITORS) {
            return t("puzzles-browse.empty-sliding-editors", "No editor's picks yet.");
        }
        return t("puzzles-browse.empty-sliding");
    })();

    const tagChipRows = useMemo(
        () =>
            tags
                .map((row) => {
                    const lbl = tagLabelFromRow(row);
                    if (!lbl) return null;
                    return { raw: lbl, label: formatBrowseTagDisplayLabel(lbl) };
                })
                .filter(Boolean),
        [tags]
    );

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Paper
                elevation={2}
                sx={{
                    mb: 3,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 2,
                    background: SLIDING_HERO_GRADIENT,
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(230, 81, 0, 0.28)",
                }}
            >
                <ViewModuleIcon
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 120,
                        color: "rgba(255,255,255,0.14)",
                        pointerEvents: "none",
                        display: { xs: "none", md: "block" },
                    }}
                    aria-hidden
                />
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontWeight: 700, mb: 0.5, position: "relative", zIndex: 1 }}
                >
                    {t("puzzles-browse.page-title-sliding")}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, position: "relative", zIndex: 1 }}>
                    {t("puzzles-browse.page-subtitle-sliding")}
                </Typography>
            </Paper>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                        <Chip
                            icon={<FiberNewIcon />}
                            label={t("puzzles-browse.filter-sliding-new5", "New 5")}
                            clickable
                            size="small"
                            variant={feed === FEED_NEW5 ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_NEW5}
                            onClick={() => handleSelectFeed(FEED_NEW5)}
                            sx={heroFeaturedChipSx(feed === FEED_NEW5, "new5")}
                        />
                        <Chip
                            icon={<WhatshotIcon />}
                            label={t("puzzles-browse.filter-sliding-top5", "Top 5")}
                            clickable
                            size="small"
                            variant={feed === FEED_TOP5 ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_TOP5}
                            onClick={() => handleSelectFeed(FEED_TOP5)}
                            sx={heroFeaturedChipSx(feed === FEED_TOP5, "top5")}
                        />
                        <Chip
                            icon={<StarIcon />}
                            label={t("puzzles-browse.filter-sliding-editors", "Editor's picks")}
                            clickable
                            size="small"
                            variant={feed === FEED_EDITORS ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_EDITORS}
                            onClick={() => handleSelectFeed(FEED_EDITORS)}
                            sx={heroFeaturedChipSx(feed === FEED_EDITORS, "editors")}
                        />
                        <Chip
                            icon={<AppsIcon />}
                            label={t("puzzles-browse.tag-all", "All")}
                            clickable
                            size="small"
                            variant={feed === FEED_BROWSE ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_BROWSE}
                            onClick={() => handleSelectTag(TAG_ALL)}
                            sx={heroFeaturedChipSx(feed === FEED_BROWSE, "all")}
                        />
            </Box>

            {isFeaturedFeed ? null : (
                <BrowseAccessFilter compact value={accessFilter} onChange={handleAccessFilter} t={t} />
            )}

            {isFeaturedFeed ? (
                <Box
                    sx={{
                        mb: 2.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor:
                            feed === FEED_NEW5
                                ? "rgba(2, 132, 199, 0.35)"
                                : feed === FEED_TOP5
                                  ? "rgba(245, 158, 11, 0.35)"
                                  : "rgba(13, 148, 136, 0.35)",
                        background:
                            feed === FEED_NEW5
                                ? "linear-gradient(120deg, rgba(2, 132, 199, 0.14) 0%, rgba(2, 132, 199, 0.04) 55%, transparent 100%)"
                                : feed === FEED_TOP5
                                  ? "linear-gradient(120deg, rgba(245, 158, 11, 0.14) 0%, rgba(245, 158, 11, 0.04) 55%, transparent 100%)"
                                  : "linear-gradient(120deg, rgba(13, 148, 136, 0.14) 0%, rgba(13, 148, 136, 0.04) 55%, transparent 100%)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
                        {feed === FEED_NEW5 ? (
                            <FiberNewIcon sx={{ color: "#0284c7", fontSize: 32 }} />
                        ) : feed === FEED_TOP5 ? (
                            <WhatshotIcon sx={{ color: "#f59e0b", fontSize: 32 }} />
                        ) : (
                            <StarIcon sx={{ color: "#0d9488", fontSize: 32 }} />
                        )}
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: "-0.02em",
                                lineHeight: 1.15,
                                color:
                                    feed === FEED_NEW5 ? "#0369a1" : feed === FEED_TOP5 ? "#b45309" : "#0f766e",
                                ...browseTextSx,
                            }}
                        >
                            {feed === FEED_NEW5
                                ? t("puzzles-browse.title-sliding-new5", "New 5")
                                : feed === FEED_TOP5
                                  ? t("puzzles-browse.title-sliding-top5", "Top 5 favorites")
                                  : t("puzzles-browse.title-sliding-editors", "Editor's picks")}
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            maxWidth: 720,
                            lineHeight: 1.55,
                            ...browseTextSx,
                        }}
                    >
                        {feed === FEED_NEW5
                            ? t(
                                  "puzzles-browse.blurb-sliding-new5",
                                  "Five recent sliding puzzles that are not the same pictures as Jigsaw New 5.",
                              )
                            : feed === FEED_TOP5
                              ? t(
                                    "puzzles-browse.blurb-sliding-top5",
                                    "The five puzzles players finish most often right now. Jump in and see why they're crowd favorites.",
                                )
                              : t(
                                    "puzzles-browse.blurb-sliding-editors",
                                    "Hand-picked by our team for beautiful photos, satisfying slides, and a memorable play. Fresh picks land here first.",
                                )}
                    </Typography>
                </Box>
            ) : (
            <BrowseTagScrollRow
                ariaLabel={t("puzzles-browse.filters-tags", "Tags")}
                scrollLeftLabel={t("puzzle.tags.scroll-left", "Scroll left")}
                scrollRightLabel={t("puzzle.tags.scroll-right", "Scroll right")}
            >
                {tagsLoading ? (
                    <>
                        <Skeleton variant="rounded" width={64} height={26} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={88} height={26} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={96} height={26} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={72} height={26} sx={{ borderRadius: 16, flexShrink: 0 }} />
                    </>
                ) : (
                    <>
                        <Chip
                            label={t("puzzles-browse.tag-all", "All")}
                            clickable
                            size="small"
                            onClick={() => handleSelectTag(TAG_ALL)}
                            color={selectedTag === TAG_ALL ? "primary" : "default"}
                            variant={selectedTag === TAG_ALL ? "filled" : "outlined"}
                            sx={{
                                ...browseTagChipSx(selectedTag === TAG_ALL),
                                flexShrink: 0,
                                height: 26,
                                minHeight: 26,
                                "& .MuiChip-label": { px: 0.75, fontSize: "0.75rem" },
                            }}
                        />
                        {tagChipRows.map(({ raw, label }) => {
                            const selected = selectedTag === raw;
                            return (
                                <Chip
                                    key={raw}
                                    avatar={<TagChipAvatar tag={raw} size={16} />}
                                    label={label}
                                    clickable
                                    size="small"
                                    onClick={() => handleSelectTag(raw)}
                                    color={selected ? "primary" : "default"}
                                    variant={selected ? "filled" : "outlined"}
                                    sx={{
                                        ...browseTagChipSx(selected),
                                        flexShrink: 0,
                                        height: 26,
                                        minHeight: 26,
                                        "& .MuiChip-label": { px: 0.75, fontSize: "0.75rem" },
                                        "& .MuiChip-avatar": { width: 16, height: 16 },
                                    }}
                                />
                            );
                        })}
                    </>
                )}
            </BrowseTagScrollRow>
            )}

            <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto" }}>
                {listLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Typography sx={{ py: 4, textAlign: "center", ...browseTextSx }}>
                        {emptyMessage}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                                lg: "repeat(5, minmax(0, 1fr))",
                            },
                            gap: { xs: 2, md: 2.5 },
                            justifyContent: "center",
                            alignItems: "stretch",
                        }}
                    >
                        {rows.map((group, idx) => {
                            const storyId = getSlidingGroupStoryId(group);
                            const story = group?.story;
                            const cardPuzzle = homeSlidingGroupToCardPuzzle(group);
                            const boardPreview = pickSlidingBrowseBoardPreview(group);
                            const imageUrl = pickSlidingStoryIcon200Url(story) || pickSlidingCardImageUrl(story);
                            const titleFallback = getSlidingStoryTitle(story) || storyId || "—";
                            const storyTitle = getSlidingStoryTitle(story) || "";

                            const rawTags = cardPuzzle?.tags?.length
                                ? cardPuzzle.tags
                                : group?.puzzleTemplate?.tags ?? [];
                            const displayTags = rawTags
                                .filter((tag) => tag && !["bedrock", "story"].includes(String(tag).toLowerCase()))
                                .map((tag) => String(tag).trim().toLowerCase());

                            let publishedAgoStr = null;
                            if (cardPuzzle?.publishedAt) {
                                const pubMs = new Date(cardPuzzle.publishedAt).getTime();
                                if (Number.isFinite(pubMs) && pubMs > 0) {
                                    publishedAgoStr = UtilService.formatHumanizeDetailed(
                                        Math.abs(Date.now() - pubMs),
                                        lang,
                                    ) || null;
                                }
                            }

                            if (!cardPuzzle) {
                                return (
                                    <Box key={storyId || `sliding-${idx}`} sx={{ width: "100%", minWidth: 0 }}>
                                        <Typography variant="body2" sx={browseTextSx}>
                                            {titleFallback}
                                        </Typography>
                                    </Box>
                                );
                            }

                            return (
                                <Box
                                    key={storyId || `sliding-${idx}`}
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        minWidth: 0,
                                        minHeight: 0,
                                        height: "100%",
                                        overflow: "visible",
                                    }}
                                >
                                        <SlidingCard
                                            puzzle={cardPuzzle}
                                            userPayPlan={userPayPlan}
                                            subscriptionStatus={subscriptionStatus}
                                            buttonText={t("puzzle.card.view")}
                                            topBadgesOffsetY={2}
                                            hideWordCountChip
                                            browseStoriesChrome
                                            hideClusteredBubbles
                                            stackPrimaryAction
                                            actionButtonSx={browseViewButtonSx}
                                            belowDifficultySlot={
                                                <Box sx={{ width: "100%" }}>
                                                    {storyTitle ? (
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.35,
                                                                mb: 0.75,
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {storyTitle}
                                                        </Typography>
                                                    ) : null}
                                                    {(boardPreview?.imageUrl || imageUrl) ? (
                                                        <Box
                                                            sx={{
                                                                position: "relative",
                                                                width: "100%",
                                                                aspectRatio: "1 / 1",
                                                                overflow: "hidden",
                                                                bgcolor: "action.hover",
                                                                pointerEvents: "none",
                                                                borderRadius: 1,
                                                            }}
                                                        >
                                                            <StoryReferenceWithGrid
                                                                imageUrl={imageUrl || boardPreview?.imageUrl}
                                                                gridSize={boardPreview?.gridSize || 4}
                                                                alt=""
                                                                squareFrame
                                                                fillSquareParent
                                                                showEmptySlot={false}
                                                                showGridLines={false}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: "100%",
                                                                aspectRatio: "1 / 1",
                                                                bgcolor: "action.hover",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                borderRadius: 1,
                                                            }}
                                                        >
                                                            <Typography variant="caption" color="text.secondary">
                                                                {t("puzzles-browse.no-story-image")}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {(publishedAgoStr || displayTags.length > 0) && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                width: "100%",
                                                                mt: 0.75,
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                                {displayTags.map((tag) => (
                                                                    <Chip
                                                                        key={tag}
                                                                        avatar={<TagChipAvatar tag={tag} size={18} />}
                                                                        label={formatBrowseTagDisplayLabel(tag)}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ fontSize: "0.7rem", height: 20 }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                            {publishedAgoStr ? (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ flexShrink: 0 }}
                                                                >
                                                                    {t("puzzle.card.published")}: {publishedAgoStr}
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {showPager ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            mt: 3,
                            mb: 1,
                        }}
                    >
                        <IconButton
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={!canGoPrev}
                            aria-label={t("puzzles-browse.page-previous", "Previous")}
                            size="large"
                            sx={browsePagerIconSx}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography
                            variant="body2"
                            sx={{ minWidth: 72, textAlign: "center", ...browseTextSx }}
                        >
                            {page + 1} / {totalPages}
                        </Typography>
                        <IconButton
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!canGoNext}
                            aria-label={t("puzzles-browse.page-next", "Next")}
                            size="large"
                            sx={browsePagerIconSx}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                ) : null}

                {SHOW_SLIDING_APP_STORE_BADGE || SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: showPager ? 2 : 3,
                            mb: 1,
                        }}
                    >
                        <AppStorePromoPill
                            iconSrc={slidingPuzzleQuestIcon}
                            appName="Sliding Puzzle Quest"
                            tagline={t(
                                "marketing.sliding-quest-promo",
                                "Sliding tile puzzles with beautiful photo grids.",
                            )}
                        >
                            {SHOW_SLIDING_APP_STORE_BADGE ? (
                                <AppStoreBadgeLink height={36} />
                            ) : null}
                            {SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                                <GooglePlayBadgeLink height={36} />
                            ) : null}
                        </AppStorePromoPill>
                    </Box>
                ) : null}
                <PuzzleListingAbout puzzleKind="SLIDING" />
            </Box>
        </Box>
    );
}
